<script setup>
import { defineProps, defineEmits, watch, onUnmounted } from 'vue';
import { InformationCircleIcon } from '@heroicons/vue/outline';
import ElementsTooltipSingleton from '@/components/elements/ElementsTooltipSingleton';
import ElementsButton from '@/components/elements/ElementsButton.vue';
import DesignButton from './design-system/design-button.vue';


const props = defineProps({
    modelValue: Boolean,
    name: String,
    tooltip: String,
    buttonText: String,
    buttonClose :  Boolean,
    height12: Boolean,
    backgroundWhite: Boolean,
    backgroundRed: Boolean,
    disabled: Boolean,
    centerTitle: Boolean,
    maxWidth:{type:String, default:'640px'},
    minWidth:{type:String, default:'0px'},
    noneOverflowAuto: { type: Boolean, default: false },
    width800: { type: Boolean, default: false },
    buttonCenter: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue', 'buttonEvent', 'afterClose']);

let originalOverflow = '';
let originalOverflowY = '';

watch(
  () => props.modelValue,
  (isOpen) => {
    const appMain = document.getElementById('app');
    if (!appMain) return;

    if (isOpen) {
      originalOverflow = appMain.style.overflow;
      originalOverflowY = appMain.style.overflowY;
      appMain.style.overflow = 'hidden';
      appMain.style.overflowY = 'hidden';
    } else {
      appMain.style.overflow = originalOverflow;
      appMain.style.overflowY = originalOverflowY;
    }
  },
  { immediate: true }
);

onUnmounted(() => {
  const appMain = document.getElementById('app-main');
  if (!appMain) return;

  appMain.style.overflow = originalOverflow;
  appMain.style.overflowY = originalOverflowY;
});

onUnmounted(() => {
    const appMain = document.getElementById('app-main');
    if (appMain) {
        appMain.style.overflow = originalOverflow;
    }
});

function hidePopup() {
    emit('update:modelValue', false);
    emit('afterClose');
}

function buttonEvent() {
    emit('buttonEvent');
}

let __tooltipComponent__hidden__ = null;

function loadTooltip(ele, msgFn) {
    __tooltipComponent__hidden__.loadBasic(ele, msgFn);
}

function loadTooltipElementReady(ele) {
    if (ele == null) return; // auto-reload, so element can be null sometime.
    __tooltipComponent__hidden__ = ele;
}
</script>

<template>
    <div v-show="props.modelValue" class="fixed top-0 w-full h-full bg-transparent-basic-3 z-[80] flex justify-center items-center">
        <div class="rounded-4 shadow-card bg-white w-[90%] flex flex-col max-h-[90vh] overflow-hidden" :style="{ maxWidth: props.maxWidth, minWidth: props.minWidth }">
            <div class="p-4 pb-3 flex justify-between items-center border-b-1 border-border-basic-2 shrink-0">
                <div v-if="props.centerTitle"></div>
                <h2 class="font-heading-sm-semibold">
                    {{ props.name }}
                    <template v-if="props.tooltip">
                        <ElementsTooltipSingleton
                            :ref="
                                (el) => {
                                    loadTooltipElementReady(el);
                                }
                            " />
                        <InformationCircleIcon
                            class="h-5 w-5 ml-1 text-blue-600"
                            aria-hidden="true"
                            style="display: inline"
                            :ref="
                                (el) => {
                                    loadTooltip(el, () => {
                                        return props.tooltip;
                                    });
                                }
                            " />
                    </template>
                </h2>
                <a href="javascript:void(0)" class="ml-20" @click="hidePopup">
                    <img src="@/assets/img/app-popup-1.svg" alt="app-popup-1" />
                </a>
            </div>
            <div class="w-full overflow-auto flex-1 min-h-0" :class="[props.noneOverflowAuto ? 'overflow-hidden' : '']">
                <slot />
            </div>
            <template v-if="props.buttonText">
                <div class="w-full p-4 border-t-1 border-border-basic-2 flex gap-2 shrink-0" :class="[ props.buttonCenter ? 'justify-center' : 'justify-end']">
                    <DesignButton 
                        btnText="Cancel"
                        btnSize="MD" btnType="SECONDARY" btnVariant="BORDER" class="w-[6rem]"
                        @click-event="hidePopup" />
                    <DesignButton
                        :btnText="props.buttonText"
                        btnSize="MD" btnType="PRIMARY" btnVariant="FILL" class="min-w-[6rem]"
                        :disabled="props.disabled"
                        @click-event="buttonEvent" />
                </div>
            </template>
        </div>
    </div>
</template>
